import { useCallback, useEffect, useRef, useState } from 'react';
import {
  emit as emitBroadcastEvent,
  setConnectionStatus,
} from '../etc/broadcast-event-bus';
import { config } from '../../config/config';
import { useGetBearerToken } from './use-bearer-token';
import { useCurrentOrg } from './use-current-org';

export type EventConnectionStatus =
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'error';

/** Broadcast event shape from server. Use BroadcastEvent from @cotera/api for typed payloads. */
export type BroadcastEvent = {
  type: string;
  payload: unknown;
};

const WS_PATH = '/ws/events';
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;
const MAX_RECONNECT_ATTEMPTS = 10;
const RECOVERY_RETRY_MS = 60000;

function apiUrlToWs(apiUrl: string): string {
  const url = new URL(apiUrl);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.origin;
}

export type UseEventsOptions = {
  /** Event types to subscribe to. Can change over time; server will update subscriptions. */
  eventTypes?: string[];
  onEvent?: (event: BroadcastEvent) => void;
  enabled?: boolean;
};

export function useEvents(options: UseEventsOptions = {}): {
  lastEvent: BroadcastEvent | null;
  status: EventConnectionStatus;
  /** Update subscriptions. Call when eventTypes change. */
  subscribe: (eventTypes: string[]) => void;
  /** Manually trigger reconnection. Resets retry count. */
  retry: () => void;
} {
  const { eventTypes = [], onEvent, enabled = true } = options;
  const getBearerToken = useGetBearerToken();
  const org = useCurrentOrg();
  const [lastEvent, setLastEvent] = useState<BroadcastEvent | null>(null);
  const [status, setStatus] = useState<EventConnectionStatus>('disconnected');
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const reconnectAttemptRef = useRef(0);
  const reconnectingRef = useRef(false);
  const eventTypesRef = useRef(eventTypes);
  eventTypesRef.current = eventTypes;
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const subscribe = useCallback((types: string[]) => {
    try {
      const ws = wsRef.current;
      if (ws !== null && ws.readyState === 1) {
        ws.send(JSON.stringify({ type: 'subscribe', eventTypes: types }));
      }
    } catch (error) {
      // Ignore send errors (e.g. connection closed)
      // eslint-disable-next-line no-console
      console.warn('Failed to subscribe to events', error);
    }
  }, []);

  const connect = useCallback(() => {
    if (!enabled || org === undefined) {
      return;
    }
    const connectInternal = async () => {
      try {
        const token = await getBearerToken();
        if (token === undefined) {
          setStatus('error');
          return;
        }

        reconnectingRef.current = true;
        const existingWs = wsRef.current;
        if (existingWs !== null) {
          existingWs.onopen = null;
          existingWs.onmessage = null;
          existingWs.onerror = null;
          existingWs.onclose = null;
          existingWs.close();
          wsRef.current = null;
        }
        reconnectingRef.current = false;

        const baseUrl = apiUrlToWs(config.apiUrl);
        const url = new URL(WS_PATH, baseUrl);
        url.searchParams.set('token', token);
        url.searchParams.set('orgId', org.id);
        const initialTypes = eventTypesRef.current;
        if (initialTypes.length > 0) {
          url.searchParams.set('eventTypes', initialTypes.join(','));
        }
        const ws = new WebSocket(url.toString());
        wsRef.current = ws;
        setStatus('connecting');

        ws.onopen = () => {
          try {
            setStatus('connected');
            reconnectAttemptRef.current = 0;
          } catch {
            // Guard against setState on unmounted component
          }
        };
        ws.onmessage = (e) => {
          try {
            const event = JSON.parse(e.data) as BroadcastEvent;
            setLastEvent(event);
            onEventRef.current?.(event);
            emitBroadcastEvent(event.type, event.payload);
          } catch {
            // Ignore parse errors and handler exceptions
          }
        };
        ws.onclose = () => {
          try {
            wsRef.current = null;
            setStatus('disconnected');
            if (reconnectingRef.current || !enabledRef.current) {
              return;
            }
            const attempt = reconnectAttemptRef.current;
            reconnectAttemptRef.current += 1;
            if (attempt >= MAX_RECONNECT_ATTEMPTS) {
              setStatus('error');
              reconnectTimeoutRef.current = setTimeout(() => {
                reconnectAttemptRef.current = 0;
                void connectInternal();
              }, RECOVERY_RETRY_MS);
            } else {
              const delay = Math.min(
                RECONNECT_BASE_MS * 2 ** attempt,
                RECONNECT_MAX_MS
              );
              reconnectTimeoutRef.current = setTimeout(connectInternal, delay);
            }
          } catch {
            // Guard against exceptions in reconnect logic
          }
        };
        ws.onerror = () => {
          try {
            setStatus('error');
          } catch {
            // Guard against setState on unmounted component
          }
        };
      } catch {
        setStatus('error');
      }
    };
    void connectInternal();
  }, [enabled, org, getBearerToken]);

  const retry = useCallback(() => {
    if (reconnectTimeoutRef.current !== null) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    reconnectAttemptRef.current = 0;
    connect();
  }, [connect]);

  useEffect(() => {
    if (status === 'connected') {
      subscribe(eventTypes);
    }
  }, [status, eventTypes, subscribe]);

  useEffect(() => {
    setConnectionStatus(status === 'connected');
  }, [status]);

  useEffect(() => {
    if (enabled && org !== undefined) {
      connect();
    }
    return () => {
      reconnectingRef.current = true;
      if (reconnectTimeoutRef.current !== null) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current !== null) {
        wsRef.current.onopen = null;
        wsRef.current.onmessage = null;
        wsRef.current.onerror = null;
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
      setStatus('disconnected');
    };
  }, [enabled, org, connect]);

  return { lastEvent, status, subscribe, retry };
}
